import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { Observable } from 'rxjs';
import { Subject } from 'rxjs/internal/Subject';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';

import { NotificationBannerConfig } from './@models/notification-banner-config.model';
import { NotificationBannerType } from './@models/notification-banner-type.model';
import { MwNotificationBannerActionsDirective } from './notification-banner.directives';
import { NotificationBannerService } from './notification-banner.services';

@Component({
  selector: 'mw-notification-banner',
  templateUrl: 'notification-banner.component.html',
  styleUrls: ['./notification-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationBannerComponent implements OnInit, OnDestroy {
  @Input() alertId: string = '';
  @Input() title: string;
  @Input() icon: string;
  @Input() text: string;
  @Input() showIcon = true;
  @Input() isDisplayed = true;
  @Input() bannerClass = 'mw-custom';
  @Input() isCloseable: boolean;
  @Input() backgroundColor: string;
  @Input() color: string;

  @HostBinding('attr.style')
  public get valueAsStyle(): any {
    return this.sanitizer.bypassSecurityTrustStyle(
      `--mw-notification-banner-background: ${this.backgroundColor};--mw-notification-banner-foreground: ${this.color}`
    );
  }

  @ContentChild(MwNotificationBannerActionsDirective, {
    read: TemplateRef,
  })
  actionsTemplate?: TemplateRef<any>;
  actionsTemplateFromConfig?: TemplateRef<any>;

  @Input() enableCloseBtn: boolean;

  get config$(): Observable<NotificationBannerConfig> {
    return this.service.configs$.pipe(
      takeUntil(this.unsubscribeAll$),
      map((configs) => configs.find((x) => x.alertId === this.alertId)),
      filter((config) => !!config)
    );
  }

  private unsubscribeAll$ = new Subject<void>();

  constructor(
    readonly service: NotificationBannerService,
    private readonly sanitizer: DomSanitizer,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscribeToVisibleIds();
    this.subscribeToSubtleChanges();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }

  onClose(): void {
    if (this.isCloseable) {
      this.service.closeNotificationBanner(this.alertId);
    }
  }

  private subscribeToVisibleIds(): void {
    this.service.configs$
      .pipe(
        takeUntil(this.unsubscribeAll$),
        filter((configs) => !!configs.find((t) => t.alertId === this.alertId)),
        switchMap(() => this.service.visibleBannersIds$)
      )
      .subscribe((displayedIds) => {
        this.isDisplayed = displayedIds.includes(this.alertId);
        this.changeDetectorRef.detectChanges();
      });
  }

  private subscribeToSubtleChanges(): void {
    this.config$.subscribe((config) => {
      this.title = config.boldText;
      this.text = config.text;
      this.icon = config.iconName;
      this.isCloseable = config.isCloseable;
      this.actionsTemplateFromConfig = config.actionsTemplate;
      this.enableCloseBtn = config.enableCloseBtn;

      if (config.bannerType !== NotificationBannerType.Custom) {
        this.bannerClass =
          config.bannerType.toString() +
          (config.isSubtle ? '-subtle' : '-regular');
      }

      this.changeDetectorRef.detectChanges();
    });
  }
}
